import { Checkbox, Grid, TextField } from '@mui/material';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Subject } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { If } from '../../common/IfComponent';
import { RootState } from '../../store';
import { ExternalPricelistTreeItem } from './externalPriceTypes';
import { getExternalPricelistTree } from './redux/externalPricelistSelectors';
import { externalPricelistsSelected } from './redux/externalPricelistSlices';
type ExternalPricelistListProps = {
    multiselect: boolean;
    onSelectPricelist: ((selectedPricelists: number[]) => void) | null;
    selectedExternalPricelistIds: number[];
    header?: JSX.Element;
    listItemRenderer?: (node: ExternalPricelistTreeItem) => JSX.Element;
};

const mapStateToProps = (state: RootState) => ({
    externalPricelists: getExternalPricelistTree(state)
});

type Props = ReturnType<typeof mapStateToProps> & ExternalPricelistListProps;

const externalPricelistFilterSubject = new Subject<string>();
function shouldHidePricelist(pricelist: ExternalPricelistTreeItem, filter: string): boolean {
    return !(
        pricelist.name.toLowerCase().includes(filter.toLowerCase()) ||
        pricelist.ref.toLowerCase() === filter.toLowerCase()
    );
}

function filterPricelistTree(
    externalPricelistList: ExternalPricelistTreeItem[],
    filter: string
): ExternalPricelistTreeItem[] {
    if (filter.length < 3) {
        return externalPricelistList;
    }

    return externalPricelistList
        .filter(cust => !shouldHidePricelist(cust, filter))
        .map(cust => ({
            ...cust
        }));
}

const ExternalPricelistList = ({
    externalPricelists,
    multiselect,
    onSelectPricelist,
    selectedExternalPricelistIds,
    header,
    listItemRenderer
}: Props) => {
    useEffect(() => {
        setFilteredPricelists(externalPricelists);

        const subscription = externalPricelistFilterSubject
            .pipe(
                tap(cf => setFilter(cf)),
                debounceTime(200)
            )
            .subscribe(cf => setFilteredPricelists(filterPricelistTree(externalPricelists, cf)));

        // Important to unsubscribe on teardown so we don't create a memory leak
        return () => subscription.unsubscribe();
    }, [externalPricelists]);

    const [filter, setFilter] = useState('');
    const [filteredPricelists, setFilteredPricelists] = useState([] as ExternalPricelistTreeItem[]);
    const dispatch = useDispatch();

    const onNodeSelect = useCallback(
        (event: SyntheticEvent<Element, Event>, nodeIds: string) => {
            const nodeId = Number(nodeIds as string);
            if (onSelectPricelist != null) {
                onSelectPricelist([nodeId]);
            } else {
                dispatch(externalPricelistsSelected(nodeId));
            }
        },
        [dispatch, onSelectPricelist]
    );

    const handleOnFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        externalPricelistFilterSubject.next(event.target.value);
    };

    const isSelected = (itemId: number) => {
        return selectedExternalPricelistIds.includes(itemId);
    };

    const handleCheckboxClicked = (externalPricelistId: number, checked: boolean) => {
        if (externalPricelistsSelected == null || !Array.isArray(selectedExternalPricelistIds)) {
            return;
        }

        let selectedPlIds: number[];
        if (checked) {
            selectedPlIds = [...selectedExternalPricelistIds, externalPricelistId];
        } else {
            selectedPlIds = selectedExternalPricelistIds.filter(plId => plId !== externalPricelistId);
        }
        if (onSelectPricelist != null) {
            onSelectPricelist(selectedPlIds.map(Number));
        }
    };

    const renderer = (extPlItem: ExternalPricelistTreeItem) => {
        let label: string | JSX.Element;
        if (listItemRenderer != null) {
            label = listItemRenderer(extPlItem);
        } else {
            label = extPlItem.displayName;
        }

        return <TreeItem key={extPlItem.id} nodeId={String(extPlItem.id)} label={label} />;
    };

    return (
        <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
                <Grid item container alignItems="center">
                    <Grid item xs={3}>
                        <TextField
                            label=""
                            onChange={handleOnFilterChange}
                            value={filter}
                            placeholder="Filter"
                            variant="standard"
                        />
                    </Grid>
                    <Grid item xs={9}>
                        {header != null && (
                            <Grid item xs={12}>
                                {header}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <TreeView
                    onNodeSelect={onNodeSelect}
                    {...(multiselect
                        ? {}
                        : {
                              selected:
                                  selectedExternalPricelistIds.length > 0
                                      ? String(selectedExternalPricelistIds[0])
                                      : ''
                          })}
                >
                    <If
                        condition={multiselect}
                        then={
                            <>
                                {filteredPricelists.map(pl => (
                                    <TreeItem
                                        key={pl.id}
                                        nodeId={String(pl.id)}
                                        label={
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    maxHeight: '25px'
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isSelected(pl.id)}
                                                    onChange={(event, checked) =>
                                                        handleCheckboxClicked(pl.id, checked)
                                                    }
                                                    color="primary"
                                                />
                                                {pl.displayName}
                                            </div>
                                        }
                                    />
                                ))}
                            </>
                        }
                        else={<>{filteredPricelists.map(pl => renderer(pl))}</>}
                    />
                </TreeView>
            </Grid>
        </Grid>
    );
};

export default connect(mapStateToProps)(ExternalPricelistList);
